import { Button } from '~/elements/Button'
import { Divider } from '~/elements/Divider'
import { TextField } from '~/elements/Input'
import { GithubButton } from '~/features/auth/components/GitHubButton'
import { GoogleButton } from '~/features/auth/components/GoogleButton'
import { createUser } from '~/features/auth/stores/auth'
import { AuthLayout } from '~/layout/AuthLayout'
import { useForm, z } from '~/lib/form'
import { useStore } from '@nanostores/react'
import { $searchParams, getRoutePath } from '~/lib/router'
import { usePlatformSettings } from '~/features/auth/hooks/useAuthQueries'
import { useEffect, useMemo } from 'react'
import { Spinner } from '~/elements/Spinner.tsx'
import { toast } from '~/elements/Toast.tsx'
import type { SubmitHandler } from 'react-hook-form'

interface SignUpFormValues {
  login: string
  password: string
  passwordConfirmation: string
}

export const schema = z
  .object({
    login: z.string().min(1, 'Login is required'),
    password: z.string().min(8, 'Should be at least 8 characters long').max(32),
    passwordConfirmation: z.string().optional()
  })
  .refine((values) => values.password === values.passwordConfirmation, {
    message: 'Passwords must match',
    path: ['passwordConfirmation']
  })

function SignUpForm() {
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setError
  } = useForm<SignUpFormValues>({ schema })

  const registerUser: SubmitHandler<SignUpFormValues> = (payload) => {
    return createUser(payload).catch(() => {
      setError('login', {
        message: 'User with provided email already exists'
      })
      toast({
        title: 'Could not create user',
        description: 'User with provided email already exists'
      })
    })
  }

  return (
    <>
      <form className="flex flex-col gap-5" onSubmit={handleSubmit(registerUser)}>
        <TextField
          label="Login"
          placeholder="example@example.com"
          {...register('login')}
          autoFocus
          error={errors?.login?.message as string}
        />
        <TextField
          autoComplete="new-password"
          label="Password"
          placeholder="8-24 Characters"
          type="password"
          {...register('password')}
          error={errors?.password?.message as string}
        />
        <TextField
          autoComplete="new-password"
          label="Confirm Password"
          type="password"
          {...register('passwordConfirmation')}
          error={errors?.passwordConfirmation?.message as string}
        />

        <Button className="mt-2" loading={isSubmitting} size="large" type="submit" variant="secondary">
          Continue With Email
        </Button>
      </form>
    </>
  )
}

export function SignUpPage() {
  const { data: platformSettings, isPending: loading } = usePlatformSettings()
  const search = useStore($searchParams)
  const invite = search.invite
  const plan = search.plan

  useEffect(() => {
    if (plan && plan !== 'free') {
      sessionStorage.setItem('rushdb_plan_intent', plan)
    }
  }, [plan])

  const hasGoogleOAuth = useMemo(
    () => platformSettings?.googleOAuthEnabled,
    [platformSettings?.googleOAuthEnabled]
  )

  const hasGithubOAuth = useMemo(
    () => platformSettings?.githubOAuthEnabled,
    [platformSettings?.githubOAuthEnabled]
  )

  const hasOauth = useMemo(() => hasGoogleOAuth || hasGithubOAuth, [hasGoogleOAuth, hasGithubOAuth])

  if (loading) {
    return (
      <AuthLayout title={'Create your account'} subtitle={'Get started with RushDB'} type="signup">
        <div className="m-auto flex content-center items-center justify-between">
          <Spinner />
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={'Create your account'} subtitle={'Get started with RushDB'} type="signup">
      {hasOauth ?
        <>
          <div className="flex w-full justify-between gap-2">
            {hasGoogleOAuth ?
              <GoogleButton invite={invite} />
            : null}
            {hasGithubOAuth ?
              <GithubButton />
            : null}
          </div>
          <Divider />
        </>
      : null}

      <SignUpForm />

      <Divider />

      <Button as="a" href={getRoutePath('signin')} size="large" variant="ghost">
        Already have an account?
      </Button>
    </AuthLayout>
  )
}
